import { updateStream, deleteStream } from '@utils/graphql/creators/streams';
import { updateDevice, deleteDevice } from '@utils/graphql/creators/devices';
import { updateZone } from '@utils/graphql/creators/zones';
import { getLogger } from '@utils/general';
import { notifications } from '@utils/notifications/index';
import {
  generatePartialFailMessage,
  generateSuccessMessage,
  generateFailMessage,
} from '@utils/formatter';

const logger = getLogger('src.pages.split.streams.bulkUpdate');

const getUpdateMutation = (type = 'stream', id, updates) => {
  const mutationsByType = {
    device: updateDevice,
    zone: updateZone,
    stream: updateStream,
  };

  const mutationForType = mutationsByType[type];
  if (!mutationForType) {
    throw new Error(`Invalid type: ${type}`);
  }

  const { mutation, execute } = mutationForType(`update_${type}`, id, updates);
  mutation.addReturnProperties('id', 'name');

  return execute;
};

const getDeleteMutation = (type = 'stream') => {
  const mutationsByType = {
    stream: deleteStream,
    device: deleteDevice,
  };

  const mutationForType = mutationsByType[type];
  if (!mutationForType) {
    throw new Error(`Invalid type: ${type}`);
  }

  return mutationForType;
};

const act = async (action, type, items, updates) => {
  const results = await Promise.all(
    items.map(async (item) => {
      const failMessage = `Failed to update ${type} ${item.name}.`;
      try {
        const { error = null } = action === 'update'
          ? await getUpdateMutation(type, item.id, updates)()
          : await getDeleteMutation(type)(item);

        if (error) {
          logger.error(error, failMessage);
          return { status: 'fail', name: item.name };
        } else {
          return { status: 'success', name: item.name };
        }
      } catch (error) {
        logger.error(error, failMessage);
        return { status: 'fail', name: item.name };
      }
    })
  );

  return results;
};

export const bulkUpdate = async (type = 'update_stream', items, updates) => {
  const [action, itemType] = type.split('_');
  if (action !== 'update' && action !== 'delete') {
    throw new Error(`Invalid action: ${action}`);
  };

  const success = [];
  const fail = [];

  const actionResults = await act(action, itemType, items, updates);

  actionResults.forEach((result) => {
    if (result.status === 'success') {
      success.push(result.name);
    } else {
      fail.push(result.name);
    }
  });

  const successMessage = generateSuccessMessage(action, itemType, success);
  const failMessage = generateFailMessage(action, itemType, fail);

  if (success.length && fail.length) {
    notifications.warn(generatePartialFailMessage(successMessage, failMessage));
    logger.info(successMessage);
    logger.error(failMessage);
    return;
  }

  if (success.length) {
    notifications.success(successMessage);
    logger.info(successMessage);
    return;
  }

  if (fail.length) {
    logger.error(`Failed to ${action} ${itemType}s:`, fail);
    notifications.error(failMessage);
    return;
  }
};

export function getConfirmBtnState(selected, confirmDelete) {
  if (!selected || selected.length === 0) return 'disabled';
  return confirmDelete ? 'error' : 'default';
}